import { useAtom } from "jotai"
import { useEffect, useState } from "react"
import { showNotificationsBoxAtom } from "../../store"
import { getUnreadNotifications, markNotificationAsRead, listenToUnreadNotifications } from "../../firebase/notifications"
import { useAuth } from "../../context/authContext"

const SidebarNotifications = () => {
    const [showNotificationsBox, setShowNotificationsBox] = useAtom(showNotificationsBoxAtom)
    const [notifications, setNotifications] = useState([])
    const [loading, setLoading] = useState(true)
    const { user } = useAuth()

    // Only log when notifications change significantly
    if (notifications.length > 0) {
        console.log('=== SIDEBAR NOTIFICATIONS ===');
        console.log('Notifications count:', notifications.length);
        console.log('Show dialog:', showNotificationsBox);
    }

    useEffect(() => {
        if (user && user.uid && showNotificationsBox) {
            console.log('Loading notifications for user:', user.uid);
            setLoading(true);
            
            // Load initial notifications
            getUnreadNotifications(user.uid).then((initialNotifications) => {
                console.log('Initial notifications loaded:', initialNotifications);
                setNotifications(initialNotifications);
                setLoading(false);
            }).catch(error => {
                console.error('Error loading notifications:', error);
                setLoading(false);
            });
        }
    }, [user, showNotificationsBox]);

    // Set up real-time listener for notifications
    useEffect(() => {
        if (user && user.uid && showNotificationsBox) {
            console.log('Setting up real-time listener for notifications dialog...');
            
            const unsubscribe = listenToUnreadNotifications(user.uid, (newNotifications) => {
                console.log('=== NOTIFICATIONS DIALOG REAL-TIME UPDATE ===');
                console.log('Received notifications for dialog:', newNotifications);
                setNotifications(newNotifications);
            });
            
            return () => {
                console.log('Cleaning up notifications dialog real-time listener');
                if (unsubscribe && typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            };
        }
    }, [user, showNotificationsBox]);

    const handleNotificationClick = async (notification) => {
        try {
            console.log('Marking notification as read:', notification.id);
            await markNotificationAsRead(notification.id);
            
            // Remove from local state
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
            
            console.log('Notification marked as read successfully');
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);
        
        if (diffInHours < 1) {
            const diffInMinutes = Math.floor((now - date) / (1000 * 60));
            return `${diffInMinutes} min ago`;
        } else if (diffInHours < 24) {
            return `${Math.floor(diffInHours)} hours ago`;
        } else {
            return date.toLocaleDateString();
        }
    };

    const getNotificationIcon = (type, action) => {
        if (type === 'listing_status') {
            return action === 'approved' ? '✅' : '❌';
        }
        if (type === 'purchase_completed') {
            return '💰';
        }
        if (type === 'offer_received') {
            return '💼';
        }
        if (type === 'offer_status') {
            return action === 'accepted' ? '✅' : '❌';
        }
        return '🔔';
    };

    const getNotificationColor = (type, action) => {
        if (type === 'listing_status') {
            return action === 'approved' ? 'text-green-500' : 'text-red-500';
        }
        if (type === 'purchase_completed') {
            return 'text-green-500';
        }
        if (type === 'offer_received') {
            return 'text-blue-500';
        }
        if (type === 'offer_status') {
            return action === 'accepted' ? 'text-green-500' : 'text-red-500';
        }
        return 'text-blue-500';
    };

    console.log('Rendering notifications dialog, showNotificationsBox:', showNotificationsBox);
    
    if (!showNotificationsBox) {
        console.log('Not showing notifications dialog, showNotificationsBox is false');
        return null;
    }
    
    return (
        <>
            {/* Small notification dropdown positioned below the button */}
            <div className="fixed z-[9999] bg-[#181818] border border-gray-600 rounded-lg shadow-lg" 
                 style={{
                     top: '80px', // Position below header
                     right: '120px', // Position near notification button
                     width: '400px',
                     maxHeight: '500px',
                     overflowY: 'auto'
                 }}>
                
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-600">
                    <div className="flex items-center">
                        <span className="text-white font-semibold">Notifications ({notifications.length})</span>
                    </div>
                    <button 
                        onClick={() => setShowNotificationsBox(false)}
                        className="text-gray-400 hover:text-white"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-2">
                    {
                        loading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="text-white">Loading notifications...</div>
                            </div>
                        ) : notifications.length > 0 ? (
                            <div className="space-y-2">
                                {
                                    notifications.map((notification, index) =>
                                        <div
                                            className="p-3 bg-[#2A2A2A] rounded-lg hover:bg-[#3A3A3A] cursor-pointer transition-colors"
                                            key={`notification-${notification.id}`}
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <div className="flex items-start space-x-3">
                                                <span className="text-lg mt-1">
                                                    {getNotificationIcon(notification.type, notification.action)}
                                                </span>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-sm text-white">
                                                        {notification.message}
                                                    </div>
                                                    {notification.listingId && (
                                                        <div className="text-gray-400 text-xs mt-1">
                                                            Listing ID: {notification.listingId}
                                                        </div>
                                                    )}
                                                    <div className="text-gray-400 text-xs mt-1">
                                                        {formatDate(notification.timestamp)}
                                                    </div>
                                                </div>
                                                
                                                <button 
                                                    className="text-xs text-blue-400 hover:text-blue-300"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleNotificationClick(notification);
                                                    }}
                                                >
                                                    Mark Read
                                                </button>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        ) : (
                            <div className="text-center text-gray-400 py-8">
                                No notifications
                            </div>
                        )
                    }
                </div>
            </div>

            {/* Backdrop */}
            <div 
                className="fixed inset-0 z-[9998] bg-black bg-opacity-50" 
                onClick={() => setShowNotificationsBox(false)}
            />
        </>
    )
}

export default SidebarNotifications 